import { Link, router } from '@inertiajs/react';
import { useEffect, useMemo, useState } from 'react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';
import { ArrowRight, Plus, Trash2, X } from 'lucide-react';
import { cn } from '../../../lib/cn';

const LIST_TYPE_LABELS = {
  course: 'Course list',
  curated: 'Curated',
  awl: 'Academic Word List',
  ngsl: 'Frequency',
  sat: 'SAT / SSAT',
  gre: 'GRE',
  academic: 'Academic',
  custom: 'Custom',
  generated: 'Generated',
  personal: 'Personal',
  chapter_suggestions: 'From a book',
};

const BUCKET_BADGE = {
  not_started: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  learning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  mastered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  graduated: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

const LIST_TYPE_GROUP_ORDER = ['curated', 'awl', 'sat', 'gre', 'ngsl', 'academic', 'custom', 'generated', 'personal'];

function AssignListPicker({ student, onClose, onAssigned }) {
  const [lists, setLists] = useState(null);
  const [error, setError] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);

  const csrfToken =
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="csrf-token"]')?.content
      : '';

  useEffect(() => {
    let cancelled = false;
    fetch(`/teacher/users/${student.id}/vocabulary/assignable_lists`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setLists(data.lists || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load lists');
      });
    return () => {
      cancelled = true;
    };
  }, [student.id]);

  const grouped = useMemo(() => {
    if (!lists) return [];
    const buckets = {};
    for (const l of lists) (buckets[l.list_type] ||= []).push(l);
    const order = [...LIST_TYPE_GROUP_ORDER, ...Object.keys(buckets).filter((k) => !LIST_TYPE_GROUP_ORDER.includes(k))];
    return order.filter((k) => buckets[k]).map((k) => ({ key: k, lists: buckets[k] }));
  }, [lists]);

  const handleAssign = (list) => {
    if (submittingId) return;
    setSubmittingId(list.id);
    router.post(
      `/teacher/users/${student.id}/vocabulary/assignments`,
      { vocabulary_list_id: list.id },
      {
        onFinish: () => {
          setSubmittingId(null);
          onAssigned?.();
        },
        onError: (errs) => {
          setError(errs?.error || 'Could not assign list');
        },
      }
    );
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Assign a list to {student.first_name}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Close picker"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>}

      {lists === null && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      )}

      {lists && lists.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No assignable lists yet. Create one from the dictionary or your library, then return here.
        </p>
      )}

      {grouped.map((group) => (
        <div key={group.key} className="mb-4 last:mb-0">
          <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
            {group.key}
          </h4>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {group.lists.map((list) => (
              <div key={list.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900 dark:text-white truncate">{list.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {list.word_count} word{list.word_count === 1 ? '' : 's'}
                    {list.already_assigned ? ' · already assigned' : ''}
                  </p>
                </div>
                <button
                  onClick={() => handleAssign(list)}
                  disabled={submittingId !== null}
                  className={cn(
                    'text-xs px-2 py-1 rounded',
                    list.already_assigned
                      ? 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      : 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300',
                    submittingId !== null && 'opacity-50'
                  )}
                >
                  {submittingId === list.id
                    ? 'Assigning…'
                    : list.already_assigned
                    ? 'Re-push'
                    : 'Assign'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}

function ListsCard({ student, lists, onAssignClick }) {
  if (!lists || lists.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Lists in queue</h3>
          <button
            onClick={onAssignClick}
            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Assign vocab list
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {student.first_name} has no vocabulary lists assigned yet.
        </p>
      </Card>
    );
  }

  const handleRemoveList = (list) => {
    if (
      !confirm(
        `Remove all ${list.total} card${list.total === 1 ? '' : 's'} from "${list.name}" for ${student.first_name}? Cards added via other sources are unaffected.`
      )
    ) {
      return;
    }
    router.delete(`/teacher/users/${student.id}/vocabulary/lists/${list.id}`);
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Lists in queue
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            ({lists.length})
          </span>
        </h3>
        <button
          onClick={onAssignClick}
          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Assign vocab list
        </button>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {lists.map((list) => (
          <div key={list.id} className="py-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {list.name}
                </p>
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 flex-shrink-0">
                  {LIST_TYPE_LABELS[list.list_type] || list.list_type}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-gray-500 dark:text-gray-400">{list.total} total</span>
                {list.not_started > 0 && (
                  <span className={cn('px-1.5 py-0.5 rounded-full', BUCKET_BADGE.not_started)}>
                    {list.not_started} not started
                  </span>
                )}
                {list.learning > 0 && (
                  <span className={cn('px-1.5 py-0.5 rounded-full', BUCKET_BADGE.learning)}>
                    {list.learning} learning
                  </span>
                )}
                {list.graduated > 0 && (
                  <span className={cn('px-1.5 py-0.5 rounded-full', BUCKET_BADGE.graduated)}>
                    {list.graduated} graduated
                  </span>
                )}
              </div>
            </div>
            {list.course_id ? (
              <Link
                href={`/teacher/courses/${list.course_id}/vocabulary`}
                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 flex-shrink-0"
                title="Course lists are managed at the course level"
              >
                Manage at course <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ) : (
              <button
                onClick={() => handleRemoveList(list)}
                data-testid={`vocab-list-remove-${list.id}`}
                className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex items-center gap-1 flex-shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function CardsTable({ student, cards }) {
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return cards;
    return cards.filter((c) => c.mastery_bucket === filter);
  }, [cards, filter]);

  const handleRemoveCard = (card) => {
    if (!confirm(`Remove "${card.lemma}" from ${student.first_name}'s queue?`)) return;
    router.delete(`/teacher/users/${student.id}/vocabulary/cards/${card.id}`);
  };

  if (cards.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">All words</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">No tracked words yet.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          All words
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            ({filtered.length} of {cards.length})
          </span>
        </h3>
        <div className="flex items-center gap-1 text-xs">
          {['all', 'not_started', 'learning', 'mastered', 'graduated'].map((b) => (
            <button
              key={b}
              onClick={() => setFilter(b)}
              className={cn(
                'px-2 py-1 rounded',
                filter === b
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              )}
            >
              {b.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            <tr className="border-b border-gray-100 dark:border-gray-800">
              <th className="py-2 pr-3 font-medium">Word</th>
              <th className="py-2 pr-3 font-medium">Source</th>
              <th className="py-2 pr-3 font-medium">Bucket</th>
              <th className="py-2 pr-3 font-medium text-right">Progress</th>
              <th className="py-2 pr-3 font-medium text-right">Reps</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((card) => (
              <tr key={card.id}>
                <td className="py-2 pr-3 text-gray-900 dark:text-white font-medium">{card.lemma}</td>
                <td className="py-2 pr-3 text-gray-600 dark:text-gray-400 truncate max-w-[200px]">
                  {card.source_label || '—'}
                </td>
                <td className="py-2 pr-3">
                  <span
                    className={cn(
                      'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full',
                      BUCKET_BADGE[card.mastery_bucket] || BUCKET_BADGE.not_started
                    )}
                  >
                    {card.mastery_bucket.replace('_', ' ')}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {card.progress_percent}%
                </td>
                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {card.reps}
                </td>
                <td className="py-2 text-right">
                  <button
                    onClick={() => handleRemoveCard(card)}
                    data-testid={`vocab-card-remove-${card.id}`}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    aria-label={`Remove ${card.lemma}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function UserVocabulary({ student, vocabulary_summary, lists, cards }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <TeacherShell scrollTitle={<>{student.full_name}  ›  Vocabulary</>}>
      <TeacherPageHeader
        title={`${student.full_name} — Vocabulary`}
        subtitle={student.email}
        backLink={`/teacher/users/${student.id}`}
        backLabel="Back to profile"
        actions={
          <Link href={`/teacher/users/${student.id}/grammar`}>
            <Button variant="ghost">Grammar →</Button>
          </Link>
        }
      />

      <div className="max-w-4xl space-y-6">
        {vocabulary_summary && (
          <Card className="p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Overview</h3>
            <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400">
              <span><strong className="text-gray-900 dark:text-white">{vocabulary_summary.mastery_overview.total}</strong> tracked</span>
              <span><strong className="text-gray-900 dark:text-white">{vocabulary_summary.mastery_overview.mastered}</strong> mastered</span>
              <span><strong className="text-gray-900 dark:text-white">{vocabulary_summary.mastery_overview.learning}</strong> learning</span>
              <span><strong className="text-gray-900 dark:text-white">{vocabulary_summary.mastery_overview.not_started}</strong> not started</span>
              <span><strong className="text-gray-900 dark:text-white">{vocabulary_summary.review_counts.total}</strong> reviews ({vocabulary_summary.review_counts.correct} correct)</span>
            </div>
          </Card>
        )}

        {pickerOpen && (
          <AssignListPicker
            student={student}
            onClose={() => setPickerOpen(false)}
            onAssigned={() => setPickerOpen(false)}
          />
        )}

        <ListsCard student={student} lists={lists} onAssignClick={() => setPickerOpen(true)} />
        <CardsTable student={student} cards={cards} />
      </div>
    </TeacherShell>
  );
}
